/** @format */

import {Box, LinearProgress} from '@mui/material';

import {format} from 'date-fns';

import Alert from '@mui/material/Alert';

import WideLayout from 'layouts/WideLayout';

import {PeriodSelector} from 'components/PeriodSelector';

import {usePeriodSelector} from 'components/PeriodSelector/hooks/usePeriodSelector';

import {isCertainProvider} from 'helpers/adTypeHelpers';

import {Discreteness, Period} from 'components/PeriodSelector/model';

import {useMixpanelContext} from 'contexts/MixpanelContext';

import {useCompanyContext} from 'contexts/CompanyContext';

import {DigitalCastRoot as Root, digitalCastClasses as classes} from './style';

import DashboardWrap from './components/dashboard-wrap';
import DigitalCastBreadcrumbs from './components/digital-cast-breadcrumbs';
import {DTO_DATE_FORMAT, PERIODS, TIME_FORMAT} from './constants/common';
import {
  useGetCurrentCompanyQuery,
  useGetCurrentUserQuery,
  useGetYandexLogsQuery,
} from './graphql/__generated__';
import {useGetNotesQuery} from './components/notes/graphql/__generated__';
import {Banner} from './components/banner';

const DigitalCast = () => {
  const {mixpanel} = useMixpanelContext();
  const {currentCompany} = useCompanyContext();

  const periodProps = usePeriodSelector({
    defaultPeriod: 'current',
    defaultDiscreteness: 'day',
  });

  const {
    startDate,
    endDate,
    activePeriod,
    activeDiscreteness,
    onChangePeriod,
    onChangeStartDate,
    onChangeEndDate,
    onChangeDiscreteness,
  } = periodProps;

  const {
    data: dataCompany,
    error: errorCompany,
    loading: loadingCompany,
  } = useGetCurrentCompanyQuery();

  const isYandexCheck = isCertainProvider(
    dataCompany?.currentUser?.currentCompany?.tokens ?? [],
    'YANDEX',
  );

  const {
    data: dataLogs,
    error: errorLogs,
    loading: loadingLogs,
  } = useGetYandexLogsQuery({
    variables: {
      timeFrom: format(startDate, TIME_FORMAT),
      timeTo: format(endDate, TIME_FORMAT),
      projectUuid: dataCompany?.currentUser?.currentCompany?.uuid || '',
    },
    skip: !isYandexCheck || !dataCompany?.currentUser?.currentCompany?.uuid,
  });

  const {
    data: dataNotes,
    error: errorNotes,
    loading: loadingNotes,
    refetch: refetchNotes,
  } = useGetNotesQuery({
    variables: {
      startDate: format(startDate, DTO_DATE_FORMAT),
      endDate: format(endDate, DTO_DATE_FORMAT),
    },
  });

  const {
    loading: loadingData,
    error: errorData,
    data,
  } = useGetCurrentUserQuery({
    variables: {
      startDate: format(startDate, DTO_DATE_FORMAT),
      endDate: format(endDate, DTO_DATE_FORMAT),
    },
  });

  if (errorData) {
    return <Alert severity='error'>{errorData.message}</Alert>;
  }

  if (errorCompany) {
    return <Alert severity='error'>{errorCompany.message}</Alert>;
  }

  if (errorLogs) {
    return <Alert severity='error'>{errorLogs.message}</Alert>;
  }

  if (errorNotes) {
    return <Alert severity='error'>{errorNotes.message}</Alert>;
  }

  const loading =
    loadingData ||
    !data?.currentUser?.currentCompany ||
    loadingCompany ||
    loadingLogs ||
    loadingNotes;

  if (loading) {
    return (
      <WideLayout>
        <LinearProgress style={{flex: 1, marginTop: 16}} />
      </WideLayout>
    );
  }

  const handleDateRangeConfirm = (newStartDate: Date, newEndDate: Date) => {
    onChangeStartDate(newStartDate);
    onChangeEndDate(newEndDate);
  };

  const handleChangeDiscreteness = (newValue: Discreteness) => {
    mixpanel.track(`aggregation_${newValue}`, {project_uuid: currentCompany?.uuid});
    onChangeDiscreteness(newValue);
  };

  const handleChangePeriod = (newPeriod: Period) => {
    mixpanel.track('change_digital_cast_period', {
      project_uuid: currentCompany?.uuid,
      period: newPeriod,
    });
    onChangePeriod(newPeriod);
  };

  return (
    <WideLayout>
      <Root>
        <Box className={classes.breadcrumbs} data-testid='dc-breadcrumbs'>
          <DigitalCastBreadcrumbs name={data.currentUser.currentCompany.name} />
        </Box>

        <Box className={classes.banner}>
          <Banner />
        </Box>

        <Box className={classes.periodSelector}>
          <PeriodSelector
            startDate={startDate}
            endDate={endDate}
            activePeriod={activePeriod}
            onChangePeriod={handleChangePeriod}
            periodsModel={PERIODS}
            onDateRangeConfirm={handleDateRangeConfirm}
            activeDiscreteness={activeDiscreteness}
            onChangeDiscreteness={handleChangeDiscreteness}
          />
        </Box>
        <DashboardWrap
          logs={dataLogs?.currentUser?.yandexHistory ?? []}
          notes={dataNotes?.currentUser?.currentCompany?.digitalCastNotes ?? []}
          refetchNotes={refetchNotes}
          startDate={startDate}
          endDate={endDate}
          analyticGoals={data.currentUser.currentCompany.funnelSettings.goals ?? []}
          activePeriod={activePeriod}
          activeDiscreteness={activeDiscreteness}
          graphStatsDto={data.currentUser.currentCompany.graphStats}
          graphSensorChecks={data.currentUser.currentCompany.graphSensorChecks}
          mediaplanStatsDto={data.currentUser.currentCompany.mediaplanStats}
          mediaplansDto={data.currentUser.currentCompany.mediaplans}
          digitalCastSettings={data.currentUser.currentCompany.digitalCastSettings}
          companyId={data.currentUser.currentCompany.id}
          funnelGoalStats={data.currentUser.currentCompany.funnelGoalStats}
          stubLayers={data.currentUser.stubLayers ?? []}
        />
      </Root>
    </WideLayout>
  );
};

export default DigitalCast;
